import styled from "styled-components";
import {
    PRESENT_COLORS,
    PRESENT_GRADIENT,
    presentFocusRing,
} from "@Components/Stage/presentModeTokens";

// Values from the "Present-Dialog" board of the design canvas.
const {
    heading: HEADING,
    body: BODY,
    muted: MUTED,
    secondary: SECONDARY,
    border: LINE,
    accent: ACCENT,
    accentHover: ACCENT_HOVER,
    tint: TINT,
    outline: OUTLINE,
} = PRESENT_COLORS;
const GRADIENT = PRESENT_GRADIENT;

/**
 * The design's dialog is 880 × 1020; at the user's request it is narrower (800 wide),
 * with the design's font sizes and height. Every size scales with --pd (1px at full size), so
 * the whole dialog shrinks proportionally to fit the window instead of scrolling.
 */
export const DIALOG_WIDTH =
    "min(800px, calc(100vw - 64px), calc((100vh - 64px) * 800 / 1020))";
const s = (px: number) => `calc(${px} * var(--pd))`;

export const DialogBody = styled.div`
    --pd: calc(${DIALOG_WIDTH} / 800);
    /* Cancels the shared dialog's content padding. */
    margin: -20px -24px;
    padding: ${s(46)} ${s(44)} ${s(32)};
    /* The design's dialog is 1020px tall, with Close at the bottom. */
    min-height: ${s(1020)};
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${s(30)};
    font-family: "Inter", "Roboto", system-ui, sans-serif;
    color: ${BODY};
    &::before {
        content: "";
        position: absolute;
        inset: 0 0 auto 0;
        height: ${s(6)};
        background: ${GRADIENT};
    }
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    gap: ${s(20)};
`;

export const IconTile = styled.div`
    flex: 0 0 auto;
    width: ${s(60)};
    height: ${s(60)};
    border-radius: 14px;
    background: ${TINT};
    color: ${ACCENT};
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
        width: ${s(32)};
        height: ${s(32)};
    }
`;

export const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${s(6)};
`;

export const Title = styled.h2`
    margin: 0;
    font-size: ${s(32)};
    font-weight: 700;
    line-height: 1.2;
    color: ${HEADING};
`;

export const Subtitle = styled.div`
    font-size: ${s(20)};
    color: ${MUTED};
`;

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${s(16)};
    padding-top: ${s(26)};
    border-top: 2px solid #eef0f4;
`;

export const SectionTitle = styled.h3`
    margin: 0;
    font-size: ${s(22)};
    font-weight: 700;
    color: ${BODY};
`;

export const Cards = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${s(16)};
`;

export const Card = styled.section`
    border: 1px solid ${LINE};
    border-radius: 16px;
    padding: ${s(28)} ${s(32)};
    background: #ffffff;
    display: flex;
    flex-direction: column;
    gap: ${s(18)};
`;

export const PhoneHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${s(6)};
`;

export const PhoneTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${s(12)};
    color: ${ACCENT};
    svg {
        flex: 0 0 auto;
        width: ${s(28)};
        height: ${s(28)};
    }
`;

export const PhoneTitle = styled.h4`
    margin: 0;
    font-size: ${s(26)};
    font-weight: 700;
    color: ${HEADING};
    white-space: nowrap;
`;

export const PhoneHint = styled.div`
    padding-left: ${s(40)};
    font-size: ${s(18)};
    line-height: 1.45;
    color: ${SECONDARY};
`;

export const QRRow = styled.div`
    display: flex;
    justify-content: center;
`;

export const QRBox = styled.div`
    width: ${s(260)};
    height: ${s(260)};
    box-sizing: border-box;
    padding: ${s(10)};
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e1e4ec;
    border-radius: 14px;
    background: #ffffff;
`;

/** Shown under the QR while a phone holds the remote. */
export const ConnectedRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: ${s(4)} ${s(8)};
    font-size: ${s(15)};
    color: ${MUTED};
`;

export const DisconnectButton = styled.button`
    padding: ${s(6)} ${s(8)};
    border: 0;
    border-radius: ${s(6)};
    background: transparent;
    color: ${ACCENT};
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    &:hover:not(:disabled) {
        color: ${ACCENT_HOVER};
        text-decoration: underline;
    }
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
    ${presentFocusRing}
`;

export const LinkGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${s(8)};
`;

export const ComputerCard = styled.section`
    border: 1px solid ${LINE};
    border-radius: 16px;
    padding: ${s(18)} ${s(24)};
    background: #ffffff;
    display: flex;
    align-items: center;
    gap: ${s(28)};
`;

export const ComputerLabel = styled.div`
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: ${s(10)};
    color: ${SECONDARY};
    svg {
        flex: 0 0 auto;
        width: ${s(22)};
        height: ${s(22)};
    }
`;

export const CardTitle = styled.h4`
    margin: 0;
    font-size: ${s(20)};
    font-weight: 700;
    color: ${HEADING};
    white-space: nowrap;
`;

export const FieldLabel = styled.label`
    font-size: ${s(17)};
    font-weight: 700;
    color: ${BODY};
`;

export const LinkRow = styled.div`
    display: flex;
    gap: ${s(10)};
`;

export const LinkInput = styled.input`
    flex: 1 1 auto;
    min-width: 0;
    height: ${s(48)};
    box-sizing: border-box;
    padding: 0 ${s(14)};
    border: 1px solid ${LINE};
    border-radius: 8px;
    background: #f5f6f8;
    font: inherit;
    font-size: ${s(17)};
    color: ${BODY};
    ${presentFocusRing}
`;

export const Hint = styled.div`
    font-size: ${s(15)};
    color: ${MUTED};
`;

const OutlineBase = styled.button`
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 8px;
    border: 1px solid ${OUTLINE};
    background: #ffffff;
    color: ${ACCENT};
    font: inherit;
    cursor: pointer;
    &:hover:not(:disabled) {
        color: ${ACCENT_HOVER};
    }
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
    ${presentFocusRing}
`;

export const CopyButton = styled(OutlineBase)`
    height: ${s(48)};
    min-width: ${s(88)};
    padding: 0 ${s(16)};
    font-size: ${s(17)};
    font-weight: 500;
`;

export const PresentButton = styled(OutlineBase)`
    height: ${s(52)};
    padding: 0 ${s(28)};
    font-size: ${s(19)};
    font-weight: 700;
`;

export const Keys = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: ${s(8)};
    font-size: ${s(17)};
    color: ${MUTED};
    kbd {
        font: inherit;
        font-size: ${s(15)};
        font-weight: 700;
        color: ${BODY};
        border: 1px solid ${LINE};
        border-bottom-width: 3px;
        border-radius: 6px;
        padding: ${s(1)} ${s(8)};
    }
`;

export const Divider = styled.div`
    display: flex;
    align-items: center;
    gap: ${s(16)};
    font-size: ${s(16)};
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${MUTED};
    &::before,
    &::after {
        content: "";
        flex: 1 1 auto;
        height: 1px;
        background: #e1e4ec;
    }
`;

export const Footer = styled.div`
    margin-top: auto;
    display: flex;
    justify-content: flex-end;
`;

export const CloseButton = styled.button`
    height: ${s(52)};
    padding: 0 ${s(26)};
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${SECONDARY};
    font: inherit;
    font-size: ${s(20)};
    cursor: pointer;
    &:hover {
        color: ${BODY};
    }
    ${presentFocusRing}
`;
